import { Directive, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[lcMask]',
    standalone: true,
})
export class MaskDirective implements OnInit {
    @Input('lcMask') maskType: string = '';
    @Input('lcMaskPattern') customPattern: string = '';

    private input: HTMLInputElement;

    constructor(
        private el: ElementRef,
        private control: NgControl
    ) {
        this.input = this.el.nativeElement;
    }

    ngOnInit() {
        // Definir placeholder baseado no tipo de máscara
        if (this.maskType && !this.input.placeholder) {
            this.input.placeholder = this.getPlaceholder();
        }
    }

    @HostListener('input', ['$event'])
    onInput(event: any) {
        const value = event.target.value;
        const maskedValue = this.applyMask(value);

        if (maskedValue !== value) {
            event.target.value = maskedValue;
            this.control.control?.setValue(maskedValue, { emitEvent: false });
        }
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: any) {
        // Permitir teclas de navegação
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

        if (allowedKeys.includes(event.key)) {
            return;
        }
        // Permitir apenas números para campos com máscara
        if (this.maskType && !/\d/.test(event.key)) {
            event.preventDefault();
        }
    }

    private applyMask(value: string): string {
        if (!value) return '';

        const numericValue = this.removeNonNumeric(value);

        switch (this.maskType) {
            case 'cpf':
                return this.maskCPF(numericValue);
            case 'cnpj':
                return this.maskCNPJ(numericValue);
            case 'cep':
                return this.maskCEP(numericValue);
            case 'phone':
                return this.maskPhone(numericValue);
            case 'date':
                return this.maskDate(numericValue);
            case 'currency':
                return this.maskCurrency(numericValue);
            default:
                return value;
        }
    }

    private removeNonNumeric(value: string): string {
        return value.replace(/\D/g, '');
    }

    private maskCPF(value: string): string {
        if (value.length <= 3) return value;
        if (value.length <= 6) return `${value.slice(0, 3)}.${value.slice(3)}`;
        if (value.length <= 9) return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6)}`;
        return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6, 9)}-${value.slice(9, 11)}`;
    }

    private maskCNPJ(value: string): string {
        if (value.length <= 2) return value;
        if (value.length <= 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
        if (value.length <= 8) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
        if (value.length <= 12) return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8)}`;
        return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12, 14)}`;
    }

    private maskCEP(value: string): string {
        if (value.length <= 5) return value;
        return `${value.slice(0, 5)}-${value.slice(5, 8)}`;
    }

    private maskPhone(value: string): string {
        if (value.length <= 2) return value;
        if (value.length <= 6) return `(${value.slice(0, 2)}) ${value.slice(2)}`;
        if (value.length <= 10) return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
        return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
    }

    private maskDate(value: string): string {
        if (value.length <= 2) return value;
        if (value.length <= 4) return `${value.slice(0, 2)}/${value.slice(2)}`;
        return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    }

    private maskCurrency(value: string): string {
        // Remove tudo que não é número
        const numbers = value.replace(/\D/g, '');

        // Se não há números, retorna vazio
        if (numbers.length === 0) {
            return '';
        }

        // Converte para centavos (divide por 100)
        const cents = parseInt(numbers, 10);
        const reais = cents / 100;

        // Formata como moeda brasileira
        return reais.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    private getPlaceholder(): string {
        switch (this.maskType) {
            case 'cpf':
                return '000.000.000-00';
            case 'cnpj':
                return '00.000.000/0000-00';
            case 'cep':
                return '00000-000';
            case 'phone':
                return '(00) 00000-0000';
            case 'date':
                return '00/00/0000';
            case 'currency':
                return 'R$ 0,00';
            default:
                return '';
        }
    }
}
